import { MULTIPART_HEADERS } from '../helpers';
import {
  ActiveAnnouncement,
  Announcement,
  AnnouncementActivity,
  AnnouncementViewer,
  AnnouncementViewsPerDay,
  GetAnnouncementViewersParams,
  GetAnnouncementViewsPerDayParams,
  GetAnnouncementsHistoryParams,
  GetAnnouncementsParams,
} from '../types/api/announcements.types';
import { ResponseType } from '../types/shared.types';
import { api } from './api';

// --- Client routes (partner / sales / admin apps) -------------------------
// The backend derives app + platform from the `app-version` header, so nothing
// extra is sent. `/active` already filters to what this user should see now.

export const getActiveAnnouncements = () =>
  api.get<ResponseType<Array<ActiveAnnouncement>>>('/v1/announcements/active');

// Every whatsNew this app + platform has had, seen and expired included,
// newest first. No total: a page shorter than `limit` is the last one. Reading
// it marks nothing as seen.
export const getAnnouncementsHistory = (
  params: GetAnnouncementsHistoryParams
) =>
  api.get<ResponseType<Array<ActiveAnnouncement>>>(
    '/v1/announcements/history',
    { params }
  );

export const markAnnouncementAsSeen = (id: number) =>
  api.post<ResponseType<null>>(`/v1/announcements/${id}/mark-as-seen`);

export const dontShowAnnouncementAgain = (id: number) =>
  api.post<ResponseType<null>>(`/v1/announcements/${id}/dont-show-again`);

// --- Admin CRUD (admin app, roles admin/support) --------------------------

export const getAnnouncements = (params: GetAnnouncementsParams) =>
  api.get<ResponseType<Array<Announcement>>>('/v1/announcements', { params });

export const getAnnouncementById = (id: number) =>
  api.get<ResponseType<Announcement>>(`/v1/announcements/${id}`);

// `data` is multipart/form-data: an `announcement` JSON string part plus,
// per target, either an `image_<app>_<platform>` or a `gif_<app>_<platform>`.
export const createAnnouncement = (data: FormData) =>
  api.post<ResponseType<Announcement>>(
    '/v1/announcements',
    data,
    MULTIPART_HEADERS
  );

export const updateAnnouncement = (id: number, data: FormData) =>
  api.put<ResponseType<Announcement>>(
    `/v1/announcements/${id}`,
    data,
    MULTIPART_HEADERS
  );

export const deleteAnnouncement = (id: number) =>
  api.delete<ResponseType<null>>(`/v1/announcements/${id}`);

// --- Who saw it (admin app, roles admin/support) --------------------------

// One row per user, newest first. No total: a short page is the last one, and
// the unfiltered total is the announcement's `seenCount`.
export const getAnnouncementViewers = (
  id: number,
  params: GetAnnouncementViewersParams
) =>
  api.get<ResponseType<Array<AnnouncementViewer>>>(
    `/v1/announcements/${id}/viewers`,
    { params }
  );

export const getAnnouncementViewsPerDay = (
  id: number,
  params: GetAnnouncementViewsPerDayParams
) =>
  api.get<ResponseType<Array<AnnouncementViewsPerDay>>>(
    `/v1/announcements/${id}/views-per-day`,
    { params }
  );

// Newest first; an empty page is the end.
export const getAnnouncementActivity = (
  id: number,
  params: { page: number; limit: number }
) =>
  api.get<ResponseType<Array<AnnouncementActivity>>>(
    `/v1/announcements/${id}/activity`,
    { params }
  );
